import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";

/**
 * Tamaño máximo del payload que se guarda en `pending_submission`. El límite de un ítem de
 * DynamoDB es 400 KB; por encima de este umbral la nota se envía en el acto en vez de
 * quedar guardada, para no perder el envío.
 */
export const MAX_PENDING_SUBMISSION_BYTES = 300_000;

const DEFAULT_RETRIES = 5;
const DEFAULT_DELAY_MS = 400;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const exceedsPendingSubmissionLimit = (payload: string): boolean =>
    new Blob([payload]).size > MAX_PENDING_SUBMISSION_BYTES;

/**
 * Relee un documento recien guardado hasta que sus lineas esten completas.
 *
 * `save-edocument` escribe la cabecera y las lineas con un TransactWrite, pero las lineas
 * se leen por GSI y eso es eventualmente consistente: leer de inmediato puede devolver una
 * lista incompleta, y un documento armado sobre lineas que faltan sale mal (monto,
 * referencias, PDF).
 */
export const fetchDocumentWithCompleteLines = async <T>(
    fetchDocument: () => Promise<T>,
    countLines: (document: T) => number,
    expectedLineCount: number,
    options: { retries?: number; delayMs?: number } = {},
): Promise<T> => {
    const { retries = DEFAULT_RETRIES, delayMs = DEFAULT_DELAY_MS } = options;

    let document = await fetchDocument();
    for (let attempt = 0; attempt < retries && countLines(document) < expectedLineCount; attempt++) {
        await wait(delayMs);
        document = await fetchDocument();
    }

    if (countLines(document) < expectedLineCount) {
        throw new Error(
            "No fue posible leer todas las lineas del documento recien guardado, intente de nuevo",
        );
    }
    return document;
}

/**
 * Lee la factura con sus líneas reintentando hasta que estén todas.
 *
 * `save-edocument` escribe la factura y sus líneas con un TransactWrite y las líneas se
 * leen por GSI, que es eventualmente consistente: leer inmediatamente después de guardar
 * puede devolver una lista incompleta y la nota de crédito de descuento se construiría
 * sobre líneas que faltan.
 */
export const fetchInvoiceWithProducts = (
    invoiceId: string,
    expectedLineCount: number,
    options: { retries?: number; delayMs?: number } = {},
): Promise<InvoiceDataType> => fetchDocumentWithCompleteLines(
    () => InvoiceApi.get(invoiceId, {
        selectionSet: [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoiceProducts.*",
            "invoiceProducts.product.*",
        ]
    }),
    (invoice) => invoice.invoiceProducts?.length || 0,
    expectedLineCount,
    options,
)
